import type { ICaseFlightDelay, FlightDelayPhase } from '@/lib/delay/types';
import type {
  DisruptionDetail,
  DisruptionProgress,
  ResolutionState,
} from '@/lib/disruptions/types';

// «Operar la disrupción» — the CONTRACTS every view and panel of the screen codes to.
// The route (`operar-route.tsx`) reads the disruption once (`use-operate-data.ts`),
// derives the view model (`operate-model.ts`) and hands the SAME `IOperateData` to the
// header, the body chosen by `view.kind` and the open panel. Views never re-read the
// detail / delays / resolution-state themselves: they get them here, live (the WS keeps
// the caches fresh and the route re-reads resolution-state when the work moves).
//
// What a view reads ON ITS OWN (shared hooks, one cache per disruption):
//   - the «avisados» fold      → `useOperateNotices(data)`   (use-operate-notices.ts)
//   - the meals summary        → `useOperateMeals(data.urn)` (use-operate-meals.ts)
//   - anything else (roster, care board rows, transfers…) → the existing `lib/` hooks.
//
// Copy: the frame's words live in the `operate` namespace; each view/panel owns its own
// namespace (operateDelay, operateEscalation, operateCancel, operatePax, operateMeals,
// operateHistory). Status colours: `StatusMark` (✓ ? ✕ –) and the badge tones below.

/** Which body the screen shows — one per disruption state (`operateView`). */
export type OperateViewKind = 'loading' | 'delay' | 'escalated' | 'cancelled' | 'resolved';

/** The state colour: `delay` amber, `cancelled` rose, `resolved` emerald, `neutral` muted. */
export type OperateTone = 'neutral' | 'delay' | 'cancelled' | 'resolved';

/** The ONE state badge of the header (DEMORADO +X h / CANCELADO / TODO RESUELTO). */
export interface IOperateBadge {
  tone: OperateTone;
  /** i18n key in the `operate` namespace (`badge.*`). */
  key: string;
  /** Interpolation values of `key` (`h`, `m` for a delay). */
  params?: Record<string, number>;
}

/** One flight of the disruption (one child case ≈ one flight), joined with its delay timeline. */
export interface IOperateFlight {
  caseUrn: string;
  flightUrn?: string;
  /** Flight number as the airline prints it (`LA288`); '' when the case carries none. */
  code: string;
  origin?: string;
  destination?: string;
  /** Scheduled departure (ISO): the timeline's STD, else the case's `departureAt`. */
  std?: string;
  /** The new departure: the ETD — or the ATD once departed. Absent for a cancelled flight. */
  etd?: string;
  /** The flight left (`DEPARTED`): `etd` holds the ATD. */
  departed: boolean;
  /** The flight does not leave: cancelled, or a delay escalated to a cancellation. */
  cancelled: boolean;
  /** The delay timeline's phase — absent when the flight has no timeline (a plain cancellation). */
  phase?: FlightDelayPhase;
  /** The CURRENT delay in minutes (> 0), when the flight has a timeline. */
  delayMinutes?: number;
  /** End of the airline's grace window — only while the flight is `ESCALATED_GRACE`. */
  graceUntil?: string;
  /** Passengers of the flight's case (`DisruptionCaseRow.passengerCount`). */
  passengerCount: number;
  /** The raw timeline, for views that need more (care, hold, history). */
  delay?: ICaseFlightDelay;
}

/** The derived view model of the screen (`operateView(detail, delays, state)`). */
export interface IOperateView {
  kind: OperateViewKind;
  badge: IOperateBadge;
  /** Every flight, in the detail's case order (timelines with no case row appended). */
  flights: IOperateFlight[];
  /** The flight the header draws the time change for — the most urgent one. */
  lead: IOperateFlight | null;
  /** A provider report over the policy maximum waits for the operator (delay-limits). */
  outOfPolicy: boolean;
  /** The earliest open grace window (escalated view countdown). */
  graceUntil?: string;
  /** The badge's delay in minutes (delay view), when known. */
  delayMinutes?: number;
  /**
   * The flights still DELAYED (not cancelled, not escalated, not departed) — on an
   * escalated / cancelled screen these are the «otros vuelos demorados» whose delay
   * actions stay one tap away (`?vista=demora`, see `operateView`). `[]` when none.
   */
  delayed: IOperateFlight[];
  /**
   * True when the screen shows the DELAY view of the still-delayed flights although a
   * more urgent state (escalated / cancelled) exists — the operator asked for it
   * (`?vista=demora`). The strip under the header offers the way back.
   */
  focused: boolean;
}

/** `?vista=` — `demora` operates the still-delayed flights of a mixed disruption. */
export type OperateFocus = 'demora';

/** A `?vista=` value, or `null` when absent / unknown. */
export function parseFocus(value: string | null | undefined): OperateFocus | null {
  return value === 'demora' ? 'demora' : null;
}

/**
 * Everything a view needs, read once by the route and passed down. Views and panels
 * ONLY render once the detail is loaded, so `detail` is never undefined here.
 */
export interface IOperateData {
  /** The disruption URN, decoded (`urn:disruption:…`). */
  urn: string;
  /** `GET /v1/disruptions/:urn` — status, rollup, flightStatus, cases[] (one per flight). */
  detail: DisruptionDetail;
  /** `GET /v1/disruptions/:urn/delays` — one timeline per delayed flight; `[]` when none. */
  delays: readonly ICaseFlightDelay[];
  /** `GET …/resolution-state` — role-aware steps/KPIs; `undefined` while it loads. */
  state: ResolutionState | undefined;
  /** `GET …/progress` (WS-fed) — run status and group facts; `undefined` while it loads. */
  progress: DisruptionProgress | undefined;
  /** Client ms the progress was last written (read or WS push) — a copy cached before
   *  this mount is not yet this screen's truth («Nexa trabajando»). Absent = fresh. */
  progressAt?: number;
  /** The derived view model (kind, badge, flights, lead). */
  view: IOperateView;
}

/** The props of every body view (DelayView, EscalationView, CancelledView, ResolvedView). */
export interface IOperateViewProps {
  data: IOperateData;
}

/** The full-screen panels and the dialogs («Quién hizo qué», «¿Por qué?», «Dinero entregado»), addressed by `?panel=`. */
export type OperatePanel = 'pasajeros' | 'comidas' | 'historial' | 'porque' | 'dinero';

export const OPERATE_PANELS: readonly OperatePanel[] = ['pasajeros', 'comidas', 'historial', 'porque', 'dinero'];

/** The props of a full-screen panel (PassengersPanel, MealsPanel). */
export interface IOperatePanelProps {
  data: IOperateData;
  /** Close the panel (drops `?panel=`). */
  onClose: () => void;
}

/** The props of the «Quién hizo qué» dialog (HistoryDialog). */
export interface IOperateHistoryProps {
  data: IOperateData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The deep-link parameters that belong to an open panel: which panel, the passengers'
 * pre-filter (a chip, or a lane's cell — `etapa`) and the PNR card it opens on. They live
 * and die WITH the panel — closing one drops them all, and opening one replaces them
 * (never merges with a previous panel's leftovers, which would reopen «Pasajeros»
 * filtered or on an old card).
 */
export const PANEL_PARAMS = ['panel', 'filtro', 'etapa', 'pnr', 'solo', 'pestana'] as const;

/** `prev` with the panel's deep-link params replaced by `panel` + `extra`. Pure. */
export function withPanel(
  prev: URLSearchParams,
  panel: OperatePanel,
  extra: Partial<Record<'filtro' | 'etapa' | 'pnr' | 'solo' | 'pestana', string>> = {},
): URLSearchParams {
  const p = withoutPanel(prev);
  p.set('panel', panel);
  for (const [k, v] of Object.entries(extra)) if (v) p.set(k, v);
  return p;
}

/** `prev` with every deep-link param of a panel dropped (the panel closes). Pure. */
export function withoutPanel(prev: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams(prev);
  for (const k of PANEL_PARAMS) p.delete(k);
  return p;
}

/** A `?panel=` value, or `null` when absent / unknown. */
export function parsePanel(value: string | null | undefined): OperatePanel | null {
  return (OPERATE_PANELS as readonly string[]).includes(value ?? '') ? (value as OperatePanel) : null;
}

/** Where the screen's ✕ goes back to: the disruptions list (the screen is the default entry). */
export const OPERATE_CLOSE_PATH = '/disruptions';

/** The Tower of a disruption (the situation board's «Torre» link beside each row). */
export function towerPath(urn: string): string {
  return `/disruptions/${encodeURIComponent(urn)}/tower`;
}

/** The operating screen of a disruption — the default entry (`disruptionEntry`). */
export function operarPath(urn: string): string {
  return `/disruptions/${encodeURIComponent(urn)}/operar`;
}
